import { useState } from 'react';
import { CircularProgress, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';

import useConfigVersionQuery from '@/features/settings/hooks/useConfigVersionQuery';
import { cx } from '@/styles/muiTheme';

import ConfigDiffReview from './ConfigDiffReview';

interface RestoreVersionDialogProps {
  configKey: string;
  /** The version being restored, or null when the dialog is closed. */
  version: number | null;
  /** Pretty-printed current value — the left side of the diff. */
  currentText: string;
  currentVersion: number;
  requireReason: boolean;
  isSaving: boolean;
  serverError: string | null;
  onClose: () => void;
  onConfirm: (version: number, reason: string) => void;
}

/**
 * Confirms a restore by showing it as what it is: a diff from the current value
 * to the one being brought back.
 *
 * It reuses the save review verbatim rather than getting its own summary. A
 * restore *is* a save — same compare-and-swap, same new version number, same
 * reason on the record — and presenting it identically means an operator reads
 * one thing in both places.
 */
const RestoreVersionDialog = ({
  configKey,
  version,
  currentText,
  currentVersion,
  requireReason,
  isSaving,
  serverError,
  onClose,
  onConfirm,
}: RestoreVersionDialogProps) => {
  const [reason, setReason] = useState('');
  const query = useConfigVersionQuery(configKey, version);

  const close = () => {
    setReason('');
    onClose();
  };

  return (
    <Dialog open={version != null} onClose={isSaving ? undefined : close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: cx.text.primary }}>
        Restore {configKey} to v{version}
      </DialogTitle>
      <DialogContent>
        {query.isLoading ? (
          <Stack sx={{ alignItems: 'center', padding: 4 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <ConfigDiffReview
            before={currentText}
            after={query.data?.rawValue ?? ''}
            reason={reason}
            onReasonChange={setReason}
            requireReason={requireReason}
            nextVersion={currentVersion + 1}
            onCancel={close}
            onConfirm={() => version != null && onConfirm(version, reason)}
            isSaving={isSaving}
            serverError={serverError}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RestoreVersionDialog;
